import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpenIcon,
  FunnelIcon,
  SparklesIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ChevronRightIcon,
  LightBulbIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import api from '../../config/api';
import { toast } from 'react-hot-toast';

interface WrongAnswer {
  id: string;
  question: {
    id: string;
    title: string;
    type: string;
    difficulty: string;
    options?: string[];
  };
  userAnswer: any;
  correctAnswer: any;
  aiAnalysis?: string;
  suggestions?: string[];
  mastered: boolean;
  retryCount: number;
  subject?: string;
  topic?: string;
  createdAt: string;
}

const WrongAnswerBook: React.FC = () => {
  const [wrongAnswers, setWrongAnswers] = useState<WrongAnswer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [showMastered, setShowMastered] = useState<'all' | 'unmastered' | 'mastered'>('unmastered');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState<string | null>(null);

  // 学科列表
  const subjects = [
    { id: '', name: '全部' },
    { id: 'math', name: '数学' },
    { id: 'english', name: '英语' },
    { id: 'physics', name: '物理' },
    { id: 'chemistry', name: '化学' },
    { id: 'biology', name: '生物' }
  ];

  // 加载错题
  useEffect(() => {
    const fetchWrongAnswers = async () => {
      setLoading(true);
      try {
        const params: any = { page, limit: 20 };
        if (selectedSubject) params.subject = selectedSubject;
        if (showMastered !== 'all') {
          params.mastered = showMastered === 'mastered';
        }

        const response = await api.get('/error-analysis/wrong-answers', { params });
        const data = response.data?.data || response.data;
        setWrongAnswers(data.wrongAnswers || []);
        setTotal(data.total || 0);
      } catch (error) {
        console.error('加载错题失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWrongAnswers();
  }, [selectedSubject, showMastered, page]);

  // AI 分析
  const handleAnalyze = async (id: string) => {
    setAnalyzing(id);
    try {
      const response = await api.get(`/error-analysis/wrong-answers/${id}/analyze`);
      const analysis = response.data?.data || response.data;
      
      // 更新本地数据
      setWrongAnswers(prev => prev.map(wa => 
        wa.id === id 
          ? { ...wa, aiAnalysis: analysis.analysis, suggestions: analysis.suggestions }
          : wa
      ));
      
      toast.success('分析完成');
    } catch (error) {
      toast.error('分析失败');
    } finally {
      setAnalyzing(null);
    }
  };

  // 标记已掌握
  const handleMarkMastered = async (id: string) => {
    try {
      await api.post(`/error-analysis/wrong-answers/${id}/mastered`);
      setWrongAnswers(prev => prev.map(wa =>
        wa.id === id ? { ...wa, mastered: true } : wa
      ));
      toast.success('已标记为掌握');
    } catch (error) {
      toast.error('操作失败');
    }
  };

  // 格式化答案显示
  const formatAnswer = (answer: any, type: string, options?: string[]) => {
    if (type === 'single_choice' && options) {
      return `${String.fromCharCode(65 + answer)}. ${options[answer]}`;
    }
    if (type === 'multiple_choice' && options) {
      return (answer as number[]).map(i => String.fromCharCode(65 + i)).join(', ');
    }
    if (type === 'true_false') {
      return answer ? '正确' : '错误';
    }
    return String(answer);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 页面头部 */}
      <div className="bg-gradient-to-r from-red-500 to-pink-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <BookOpenIcon className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">错题本</h1>
              <p className="text-red-100">记录错题，针对性提高</p>
            </div>
          </div>

          {/* 统计 */}
          <div className="mt-6 grid grid-cols-3 gap-4">
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-red-100 text-sm">总错题数</p>
              <p className="text-2xl font-bold">{total}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-red-100 text-sm">待复习</p>
              <p className="text-2xl font-bold">
                {wrongAnswers.filter(wa => !wa.mastered).length}
              </p>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-red-100 text-sm">已掌握</p>
              <p className="text-2xl font-bold">
                {wrongAnswers.filter(wa => wa.mastered).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* 筛选栏 */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <FunnelIcon className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-600">筛选：</span>
            </div>

            {/* 学科筛选 */}
            <div className="flex gap-2">
              {subjects.map(subject => (
                <button
                  key={subject.id}
                  onClick={() => setSelectedSubject(subject.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    selectedSubject === subject.id
                      ? 'bg-red-100 text-red-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {subject.name}
                </button>
              ))}
            </div>

            <div className="flex-1" />

            {/* 掌握状态筛选 */}
            <div className="flex gap-2">
              {[
                { id: 'unmastered', label: '待复习' },
                { id: 'mastered', label: '已掌握' },
                { id: 'all', label: '全部' }
              ].map(filter => (
                <button
                  key={filter.id}
                  onClick={() => setShowMastered(filter.id as any)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    showMastered === filter.id
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 快捷入口 */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Link
            to="/student/learning-analysis"
            className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-2xl p-5 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold mb-1">学习分析</h3>
                <p className="text-sm text-blue-100">查看薄弱知识点和学习建议</p>
              </div>
              <ChevronRightIcon className="w-5 h-5" />
            </div>
          </Link>
          <Link
            to="/student/knowledge"
            className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-2xl p-5 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold mb-1">知识库</h3>
                <p className="text-sm text-emerald-100">系统复习相关知识点</p>
              </div>
              <ChevronRightIcon className="w-5 h-5" />
            </div>
          </Link>
        </div>

        {/* 错题列表 */}
        {loading ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto" />
            <p className="mt-3 text-gray-500">加载中...</p>
          </div>
        ) : wrongAnswers.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <CheckCircleIcon className="w-16 h-16 text-green-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">太棒了！</h3>
            <p className="text-gray-500">暂无错题记录，继续保持！</p>
          </div>
        ) : (
          <div className="space-y-4">
            {wrongAnswers.map(wa => (
              <div
                key={wa.id}
                className={`bg-white rounded-2xl shadow-sm overflow-hidden border-l-4 ${
                  wa.mastered ? 'border-green-500' : 'border-red-500'
                }`}
              >
                {/* 题目头部 */}
                <div
                  className="p-5 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedId(expandedId === wa.id ? null : wa.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          wa.question.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                          wa.question.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {wa.question.difficulty === 'easy' ? '简单' : 
                           wa.question.difficulty === 'medium' ? '中等' : '困难'}
                        </span>
                        {wa.topic && (
                          <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                            {wa.topic}
                          </span>
                        )}
                        {wa.mastered && (
                          <span className="flex items-center gap-1 text-green-600 text-xs">
                            <CheckCircleSolidIcon className="w-4 h-4" />
                            已掌握
                          </span>
                        )}
                      </div>
                      <h3 className="text-gray-900 font-medium">{wa.question.title}</h3>
                    </div>
                    <ChevronRightIcon className={`w-5 h-5 text-gray-400 transition-transform ${
                      expandedId === wa.id ? 'rotate-90' : ''
                    }`} />
                  </div>
                </div>

                {/* 展开内容 */}
                {expandedId === wa.id && (
                  <div className="border-t bg-gray-50 p-5">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-red-50 rounded-xl p-4">
                        <p className="text-xs text-red-600 font-medium mb-1">你的答案</p>
                        <p className="text-red-800">
                          {formatAnswer(wa.userAnswer, wa.question.type, wa.question.options)}
                        </p>
                      </div>
                      <div className="bg-green-50 rounded-xl p-4">
                        <p className="text-xs text-green-600 font-medium mb-1">正确答案</p>
                        <p className="text-green-800">
                          {formatAnswer(wa.correctAnswer, wa.question.type, wa.question.options)}
                        </p>
                      </div>
                    </div>

                    {/* AI 分析 */}
                    {wa.aiAnalysis ? (
                      <div className="bg-blue-50 rounded-xl p-4 mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <SparklesIcon className="w-5 h-5 text-blue-600" />
                          <span className="font-medium text-blue-900">AI 分析</span>
                        </div>
                        <p className="text-gray-700 mb-3">{wa.aiAnalysis}</p>
                        {wa.suggestions && wa.suggestions.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-blue-800 mb-2">学习建议：</p>
                            <ul className="space-y-1">
                              {wa.suggestions.map((s, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                                  <LightBulbIcon className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                                  {s}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => handleAnalyze(wa.id)}
                        disabled={analyzing === wa.id}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors mb-4"
                      >
                        {analyzing === wa.id ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700" />
                            分析中...
                          </>
                        ) : (
                          <>
                            <SparklesIcon className="w-4 h-4" />
                            AI 分析错因
                          </>
                        )}
                      </button>
                    )}

                    {/* 操作按钮 */}
                    <div className="flex gap-3">
                      {!wa.mastered && (
                        <button
                          onClick={() => handleMarkMastered(wa.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <CheckCircleIcon className="w-4 h-4" />
                          标记已掌握
                        </button>
                      )}
                      <Link
                        to={`/student/practice?questionId=${wa.question.id}`}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <ArrowPathIcon className="w-4 h-4" />
                        重做此题
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* 分页 */}
        {total > 20 && (
          <div className="mt-6 flex justify-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50"
            >
              上一页
            </button>
            <span className="px-4 py-2 text-gray-600">
              第 {page} 页，共 {Math.ceil(total / 20)} 页
            </span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page >= Math.ceil(total / 20)}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50"
            >
              下一页
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WrongAnswerBook;
